import React, { useCallback, useMemo } from 'react';
import { BalancingFieldRow, BalancingFieldRowInfo } from './balancingLib';
import { ColumnsType } from 'antd/es/table';
import { computeRemSize } from '@ydistri/theme';
import { useGetBalancingConfigurationQuery } from './apiBalancingConfiguration';
import {
  boundsOf,
  useGetConfigurationFieldSpecificationsQuery,
} from '../../../apis/apiConfigurationFieldSpecifications';
import {
  CalculationConfigurationExperimentalType,
  ConfigurationFieldOwner,
} from '@ydistri/api-sdk';
import BalancingField from './BalancingField';
import LoadingIndicator from '../../../components/global/LoadingIndicator/LoadingIndicator';
import { useUser } from '../../../hooks/useUser';
import useIsConfigurationEditable from '../../../hooks/useIsConfigurationEditable';
import { DataTable, Text } from '@ydistri/ds';
import { Button } from 'antd';
import { useTemplateOrCalculationWithValue } from '../../../hooks/useTemplateOrCalculationWithValue';

interface BalancingFieldTableProps {
  fieldKey: CalculationConfigurationExperimentalType;
}

const BalancingFieldTable: React.FC<BalancingFieldTableProps> = ({ fieldKey }) => {
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const isConfigurationEditable = useIsConfigurationEditable();
  const { data: balancingData, isFetching } = useGetBalancingConfigurationQuery(
    templateOrCalculation.value,
  );
  const user = useUser();

  const {
    data: specifications,
    isSuccess: boundsReady,
    isError: boundsFailed,
    refetch,
  } = useGetConfigurationFieldSpecificationsQuery();
  const retryBounds = useCallback(() => {
    void refetch();
  }, [refetch]);
  const showHeader = !!balancingData?.configuration[fieldKey]?.groupedWith;

  const usedFields = useMemo(
    () => [fieldKey, ...(balancingData?.configuration[fieldKey]?.groupedWith ?? [])],
    [balancingData?.configuration, fieldKey],
  );

  const columns: ColumnsType<BalancingFieldRow> = useMemo(() => {
    if (!balancingData) return [];

    const cols: ColumnsType<BalancingFieldRow> = [];

    usedFields.forEach(f => {
      const field = balancingData.configuration[f];
      cols.push({
        title: field?.columnLabel ?? '',
        dataIndex: ['data', f],
        width: computeRemSize(80),
        render: (value: BalancingFieldRowInfo) => (
          <BalancingField
            disabled={
              !isConfigurationEditable ||
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- it may be false
              balancingData.configuration[f]?.alwaysDisabled ||
              !user?.isSuperUser
            }
            dataType={value.type}
            field={f}
            value={value.value}
            itemSequence={value.itemSequence}
            customSuffix={value.customSuffix}
            min={value.min}
            max={value.max}
            decimalPlaces={value.decimalPlaces}
            replaceWith={value.replaceWith}
          />
        ),
      });
    });

    return cols;
  }, [balancingData, isConfigurationEditable, usedFields, user?.isSuperUser]);

  const data: BalancingFieldRow[] = useMemo(() => {
    if (!balancingData) return [];
    const vals = balancingData.configuration[fieldKey]?.values;

    return (
      vals?.map((v, i) => {
        const obj: BalancingFieldRow = {
          index: i,
          data: {},
        };

        usedFields.forEach(f => {
          const bd = balancingData.configuration[f];

          if (bd) {
            let replaceWith = undefined;
            if (bd.lastValuePlaceholder && i === vals.length - 1) {
              replaceWith = bd.lastValuePlaceholder;

              //currently, now only {prevValue} is implemented
              if (vals[i - 1]) {
                replaceWith = replaceWith.replace('{prevValue}', vals[i - 1].toString());
              }
            }

            const bounds = boundsOf(
              specifications,
              ConfigurationFieldOwner.ExperimentalConfiguration,
              f,
            );
            obj.data[f] = {
              type: bd.type,
              value: bd.values[i],
              itemSequence: i,
              customSuffix: bd.customSuffix,
              min: bounds.min,
              max: bounds.max,
              decimalPlaces: bounds.decimalPlaces,
              replaceWith,
            };
          }
        });

        return obj;
      }) ?? []
    );
  }, [balancingData, fieldKey, specifications, usedFields]);

  // The bounds gate the editors: a field without its specification is not an unbounded field.
  if (boundsFailed) {
    return (
      <Text $type="danger">
        The input bounds could not be loaded.
        <Button type="link" size="small" onClick={retryBounds}>
          Retry
        </Button>
      </Text>
    );
  }
  if (isFetching || !boundsReady) {
    return <LoadingIndicator />;
  }

  return (
    <DataTable<BalancingFieldRow>
      id={`balancing-fields-admin-${fieldKey}`}
      rowKey="key"
      dataSource={data}
      columns={columns}
      showHeader={showHeader}
      loading={false}
    />
  );
};

export default BalancingFieldTable;
